import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
  AlertIdData,
  DeliveryExceptionsReasons,
  DeliveryExceptionsReasonsPlural,
  Notification,
} from '@usf/alert-types';
import { PlatformService } from '@panamax/app-state';
import { OrderService } from '../../../../../order/services/order.service';
import { firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { NotificationMetadata } from '../../notification-details.page';

interface DeliveryException {
  reasonCode: string;
  reasonDescription: string;
  productName: string;
  productNumber: string;
  undeliveredQuantity: string;
  unitOfMeasure: string;
}

interface DeliveryIssueViewModel {
  notification: Notification;
  orderNumber: string;
  deliveryExceptions: DeliveryException[];
}

@Component({
  selector: 'app-delivery-issue',
  templateUrl: './delivery-issue.component.html',
  styleUrls: ['./delivery-issue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryIssueComponent {
  private _notification: Notification;
  @Input() notificationMetadata: NotificationMetadata;

  @Input()
  public get notification(): Notification {
    return this._notification;
  }

  public set notification(value: Notification) {
    this._notification = value;
    this.buildViewModel();
  }

  public viewModel: DeliveryIssueViewModel;

  constructor(
    public readonly platformService: PlatformService,
    private readonly orderService: OrderService,
    private readonly router: Router,
    private readonly translateService: TranslateService,
  ) {}

  private buildViewModel(): void {
    if (!this._notification) {
      return;
    }

    const orderNumber = this._notification.alertContext?.find(
      x => x.key === 'orderNumber',
    )?.value;

    const exceptions: DeliveryException[] =
      this._notification.alertContext?.find(
        x => x.key === 'deliveryExceptions',
      )?.value;

    const reasons = exceptions
      ?.map((d: any) => d.reasonCode)
      .reduce(
        (unique: any[], item: any) =>
          unique.includes(item) ? unique : [...unique, item],
        [],
      );

    const deliveryExceptions: DeliveryException[] = [];
    const deliveryExceptionGroups: DeliveryException[][] = [];
    reasons?.forEach((reason: string) => {
      deliveryExceptionGroups.push(
        exceptions?.filter((de: DeliveryException) => de.reasonCode === reason),
      );
    });

    deliveryExceptionGroups.forEach(de => {
      let deliveryException: DeliveryException;
      if (de.length > 1) {
        deliveryException = {
          ...de[0],
          reasonDescription: this.translateService.instant(
            DeliveryExceptionsReasonsPlural[de[0].reasonCode],
          ),
        };
      } else {
        deliveryException = {
          ...de[0],
          reasonDescription: this.translateService.instant(
            DeliveryExceptionsReasons[de[0].reasonCode],
          ),
        };
      }
      deliveryExceptions.push(deliveryException);
    });

    const arrayOrder = ['04', '01', '03', '08'];
    deliveryExceptions.sort(
      (a, b) =>
        arrayOrder.indexOf(a[0]?.reasonCode) -
        arrayOrder.indexOf(b[0]?.reasonCode),
    );

    this.viewModel = {
      notification: this._notification,
      orderNumber: orderNumber || '',
      deliveryExceptions,
    };
  }

  navigateTo(notification: Notification) {
    const orderNumber = notification?.alertContext?.find(
      n => n.key === 'orderNumber',
    );
    const params = AlertIdData.find(
      a => a.alertId === notification?.alertId,
    )?.url?.split('?')[1];
    firstValueFrom(
      this.orderService?.selectOrderWithTandemNumber(
        Number(orderNumber?.value),
      ),
    ).then(order => {
      if (order) {
        const path =
          `${
            this.platformService.isTouch.value
              ? '/touch/order/submitted-order/'
              : '/desktop/order/submitted-order/'
          }` +
          `${order.orderHeader.customerNumber}:` +
          `${order.orderHeader.departmentNumber}:` +
          `${order.orderHeader.orderId}` +
          `?${params}`;
        this.router.navigateByUrl(path);
      } else {
        this.router.navigateByUrl('my-orders?' + params);
      }
    });
  }
}
